/**
 * RegionMastery (Fase 10 · §30-44) — maestria POR REGIÃO, ganha JOGANDO na região
 * (kills, elites, boss, discoveries, chests, Rift Surges). Nunca comprada, nunca por AFK.
 * Recompensas play-to-progress; perks region-scoped moderados; desbloqueia tiers de Surge.
 * Segue o padrão de estado persistível (snapshot/load) das outras progressões.
 */
import { GameConfig } from "../config/GameConfig";

export interface RegionMasteryEntry {
  xp: number; // XP acumulada DENTRO do nível atual
  level: number;
}
export type RegionMasterySnapshot = Record<string, RegionMasteryEntry>;

const cfg = GameConfig.regionMastery;

export class RegionMastery {
  private readonly data = new Map<string, RegionMasteryEntry>();

  private entry(region: string): RegionMasteryEntry {
    let e = this.data.get(region);
    if (!e) {
      e = { xp: 0, level: 0 };
      this.data.set(region, e);
    }
    return e;
  }

  level(region: string): number {
    return this.entry(region).level;
  }
  xpInto(region: string): number {
    return this.entry(region).xp;
  }
  xpForNext(region: string): number {
    const e = this.entry(region);
    return e.level >= cfg.maxLevel ? 0 : cfg.xpPerLevel(e.level + 1);
  }
  isMaxed(region: string): boolean {
    return this.entry(region).level >= cfg.maxLevel;
  }

  /** Adiciona XP (ação válida); retorna os níveis recém-alcançados p/ conceder recompensas. */
  gain(region: string, amount: number): number[] {
    if (amount <= 0) return [];
    const e = this.entry(region);
    if (e.level >= cfg.maxLevel) return [];
    const reached: number[] = [];
    e.xp += amount;
    while (e.level < cfg.maxLevel) {
      const need = cfg.xpPerLevel(e.level + 1);
      if (e.xp < need) break;
      e.xp -= need;
      e.level++;
      reached.push(e.level);
    }
    if (e.level >= cfg.maxLevel) e.xp = 0;
    return reached;
  }

  /** Maior tier de Surge desbloqueado: I = concluída; II/III por maestria (§37). */
  surgeMaxTier(region: string, completed: boolean): number {
    if (!completed) return 0;
    const lv = this.level(region);
    if (lv >= cfg.surgeUnlock.tier3) return 3;
    if (lv >= cfg.surgeUnlock.tier2) return 2;
    return 1;
  }

  /** Multiplicador de essência ganho SÓ nesta região (moderado, §43). */
  essenceMult(region: string): number {
    const lv = this.level(region);
    let m = 1;
    if (lv >= 2) m += cfg.perks.essenceAtLv2;
    if (lv >= 20) m += cfg.perks.essenceAtLv20;
    return m;
  }
  /** Bônus de chance de loot SÓ nesta região (moderado, §43). */
  lootBonus(region: string): number {
    const lv = this.level(region);
    let b = 0;
    if (lv >= 6) b += cfg.perks.lootAtLv6;
    if (lv >= 20) b += cfg.perks.lootAtLv20;
    return b;
  }

  snapshot(): RegionMasterySnapshot {
    const out: RegionMasterySnapshot = {};
    for (const [k, v] of this.data) out[k] = { xp: v.xp, level: v.level };
    return out;
  }
  load(snap: RegionMasterySnapshot | undefined): void {
    this.data.clear();
    if (!snap) return;
    for (const k of Object.keys(snap)) {
      const v = snap[k];
      if (v && typeof v.xp === "number" && typeof v.level === "number") {
        this.data.set(k, { xp: Math.max(0, v.xp), level: Math.max(0, Math.min(cfg.maxLevel, Math.floor(v.level))) });
      }
    }
  }
  reset(): void {
    this.data.clear();
  }
}
